import { Controller } from "@hotwired/stimulus"

// Master-Detail Side Panel controller.
//
// Turns a standard data_table index page into a master-detail layout:
// clicking a table row loads the record's detail view in a slide-in panel
// without navigating away from the list.
//
// Usage:
//   <div data-controller="master-detail"
//        data-master-detail-breakpoint-value="992">
//     <div class="master-detail-layout">
//       <div class="master-detail-master" data-master-detail-target="master">
//         <table>
//           <tr data-action="click->master-detail#select"
//               data-master-detail-url-param="/products/1/panel"
//               data-master-detail-show-url-param="/products/1">
//           </tr>
//         </table>
//       </div>
//       <%= render 'shared/detail_panel' %>
//     </div>
//   </div>
//
export default class extends Controller {
  static targets = ["master", "panel", "frame", "close", "fullLink"]
  static values = {
    breakpoint: { type: Number, default: 992 } // lg breakpoint
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)

    // Listen for turbo:frame-load to hide spinner after content loads
    this.element.addEventListener("turbo:frame-load", this.handleFrameLoad.bind(this))
    this.element.addEventListener("turbo:before-fetch-request", this.handleBeforeFetch.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  // Action: called when a table row is clicked
  select(event) {
    // Don't intercept clicks on links, buttons, or form elements
    if (event.target.closest("a, button, input, select, textarea, .dropdown-menu")) return

    const row = event.currentTarget
    const url = event.params.url
    const showUrl = event.params.showUrl

    // On mobile, navigate to the full page instead
    if (window.innerWidth < this.breakpointValue) {
      if (showUrl) window.location.href = showUrl
      return
    }

    event.preventDefault()
    this.loadRecord(row, url, showUrl)
  }

  // Load a record into the detail panel
  loadRecord(row, panelUrl, showUrl) {
    if (!panelUrl) return

    // Highlight the selected row
    this.clearSelection()
    row.classList.add("master-detail-selected")

    // Update the "View full page" link
    if (this.hasFullLinkTarget && showUrl) {
      this.fullLinkTarget.href = showUrl
    }

    // Show loading state
    this.showPanel()
    this.showLoading()

    // Load content via Turbo Frame
    if (this.hasFrameTarget) {
      this.frameTarget.src = panelUrl
    }
  }

  // Close the detail panel
  close() {
    this.hidePanel()
    this.clearSelection()
  }

  // Keyboard navigation
  handleKeydown(event) {
    // Only handle when panel is open and no input is focused
    if (!this.isPanelOpen()) return
    if (document.activeElement.closest("input, select, textarea, [contenteditable]")) return

    const rows = this.selectableRows()
    if (!rows.length) return

    const currentIndex = this.currentSelectedIndex(rows)

    switch (event.key) {
      case "ArrowDown":
      case "j":
        event.preventDefault()
        this.selectByIndex(rows, currentIndex + 1)
        break
      case "ArrowUp":
      case "k":
        event.preventDefault()
        this.selectByIndex(rows, currentIndex - 1)
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  // Turbo Frame event handlers
  handleBeforeFetch(event) {
    if (event.target === this.frameTarget) {
      this.showLoading()
    }
  }

  handleFrameLoad(event) {
    if (event.target === this.frameTarget) {
      this.hideLoading()
    }
  }

  // --- Private helpers ---

  selectableRows() {
    if (!this.hasMasterTarget) return []
    return Array.from(this.masterTarget.querySelectorAll("tr[data-action*='master-detail#select']"))
  }

  currentSelectedIndex(rows) {
    return rows.findIndex(r => r.classList.contains("master-detail-selected"))
  }

  selectByIndex(rows, index) {
    if (index < 0) index = 0
    if (index >= rows.length) index = rows.length - 1

    const row = rows[index]
    if (!row) return

    // Extract params from the row's data attributes
    const url = row.dataset.masterDetailUrlParam
    const showUrl = row.dataset.masterDetailShowUrlParam

    this.loadRecord(row, url, showUrl)

    // Scroll row into view if needed
    row.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  clearSelection() {
    this.element.querySelectorAll(".master-detail-selected").forEach(el => {
      el.classList.remove("master-detail-selected")
    })
  }

  showPanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("master-detail-panel-open")
    }
    if (this.hasMasterTarget) {
      this.masterTarget.classList.add("master-detail-master-shrunk")
    }
  }

  hidePanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("master-detail-panel-open")
    }
    if (this.hasMasterTarget) {
      this.masterTarget.classList.remove("master-detail-master-shrunk")
    }
    // Clear the frame content
    if (this.hasFrameTarget) {
      this.frameTarget.replaceChildren()
      this.frameTarget.removeAttribute("src")
    }
  }

  isPanelOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains("master-detail-panel-open")
  }

  showLoading() {
    if (!this.hasPanelTarget) return
    const loader = this.panelTarget.querySelector(".master-detail-loading")
    const content = this.panelTarget.querySelector(".master-detail-content")
    if (loader) loader.style.display = "flex"
    if (content) content.style.display = "none"
  }

  hideLoading() {
    if (!this.hasPanelTarget) return
    const loader = this.panelTarget.querySelector(".master-detail-loading")
    const content = this.panelTarget.querySelector(".master-detail-content")
    if (loader) loader.style.display = "none"
    if (content) content.style.display = "block"
  }
}
